<template>
  <div>
    <div class="text-h6 mb-4">AI Models</div>

    <!-- Admin context (opened from /admin): the org's allowlist, picked from the
         full catalog. -->
    <template v-if="mode === 'admin'">
      <div class="text-subtitle-2 font-weight-bold mb-1">Allowed models for this organization</div>
      <div class="text-caption text-disabled mb-2">
        The models this organization is permitted to use. The org owner enables a subset of these
        for their team. Empty = the org has no AI models.
      </div>
      <v-select
        v-model="localAdminAllowedVersions"
        :items="allModels"
        item-title="name"
        item-value="version"
        label="Allowed models"
        variant="outlined"
        density="default"
        multiple
        chips
        closable-chips
        hide-details
        class="mb-3"
        :disabled="savingAdmin"
      />
      <v-btn color="primary" variant="flat" :loading="savingAdmin" @click="saveAdminAllowed">Save allowed models</v-btn>
    </template>

    <!-- Owner context (opened from the org home): enable a subset + pick the default. -->
    <template v-else>
      <div v-if="adminAllowedModels.length === 0" class="text-body-2 text-medium-emphasis font-italic">
        No models have been enabled for your organization yet — contact your platform administrator.
      </div>
      <template v-else>
        <div class="mb-6">
          <div class="text-subtitle-2 font-weight-bold mb-1">Models enabled for the team</div>
          <div class="text-caption text-disabled mb-2">
            Which of your organization's allowed models the team can use when creating workflows.
            Leave empty to fall back to just the default model.
          </div>
          <v-select
            v-model="localAllowedVersions"
            :items="adminAllowedModels"
            item-title="name"
            item-value="version"
            label="Enabled models"
            variant="outlined"
            density="default"
            multiple
            chips
            closable-chips
            hide-details
            :disabled="saving"
          />
        </div>

        <div class="mb-6">
          <div class="text-subtitle-2 font-weight-bold mb-1">Default model for new workflows</div>
          <v-select
            v-model="localDefaultVersion"
            :items="adminAllowedModels"
            item-title="name"
            item-value="version"
            label="Default model"
            variant="outlined"
            density="default"
            clearable
            hide-details
            :disabled="saving"
          />
        </div>

        <v-btn color="primary" variant="flat" :loading="saving" @click="save">Save AI Settings</v-btn>
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { AI_MODELS } from '@/constants/aiModels'

const props = defineProps({
  orgId: { type: String, required: true },
  // 'admin' = edit the org's allowlist (from /admin); 'owner' = enable a subset + default.
  mode: { type: String, default: 'owner' }
})

const store = useStore()

const allModels = AI_MODELS

const org = computed(() => store.state.organisations.organisations?.find(o => o.id === props.orgId))

// The org's allowed models (set by a Super Admin) — the options the owner enables
// a subset of, and picks the default from.
const adminAllowedModels = computed(() => {
  const versions = org.value?.adminAllowedAiVersions || []

  return allModels.filter(m => versions.includes(m.version))
})

// The versions that are both admin-allowed AND present in the model catalog —
// exactly the options shown in the owner dropdowns. A selection outside this set
// would render as a raw version number, so we always prune selections to it.
const knownAllowedVersions = (o) => {
  const admin = o?.adminAllowedAiVersions || []

  return admin.filter((v) => allModels.some((m) => m.version === v))
}

// Drop stale enabled selections left over after the admin narrows the allowlist.
const cleanEnabled = (o) => {
  const allowed = knownAllowedVersions(o)

  return (o?.allowedAiVersions || []).filter((v) => allowed.includes(v))
}

// The default must also be a currently-allowed, known model — else clear it.
const cleanDefault = (o) => {
  const allowed = knownAllowedVersions(o)
  const current = o?.defaultAiVersion ?? null

  return current != null && allowed.includes(current) ? current : null
}

const localAllowedVersions = ref(cleanEnabled(org.value))
const localDefaultVersion = ref(cleanDefault(org.value))
const localAdminAllowedVersions = ref([...(org.value?.adminAllowedAiVersions || [])])
const saving = ref(false)
const savingAdmin = ref(false)

watch(org, (newOrg) => {
  if (newOrg) {
    localAllowedVersions.value = cleanEnabled(newOrg)
    localDefaultVersion.value = cleanDefault(newOrg)
    localAdminAllowedVersions.value = [...(newOrg.adminAllowedAiVersions || [])]
  }
})

// When the admin allowlist is edited in place, the org object is mutated (same
// reference), so the `org` watcher above may not fire. Also prune whenever the
// resolved option list changes — this guarantees chips never show a raw version
// number and a removed model can't linger as the default.
watch(adminAllowedModels, (models) => {
  const valid = new Set(models.map((m) => m.version))
  localAllowedVersions.value = localAllowedVersions.value.filter((v) => valid.has(v))
  if (localDefaultVersion.value != null && !valid.has(localDefaultVersion.value)) {
    localDefaultVersion.value = null
  }
})

const saveAdminAllowed = async () => {
  savingAdmin.value = true
  await store.dispatch('organisations/adminUpdateOrganisationAiSettings', {
    id: props.orgId,
    adminAllowedAiVersions: localAdminAllowedVersions.value
  })
  savingAdmin.value = false
}

const save = async () => {
  saving.value = true
  await store.dispatch('organisations/updateOrganisationAiSettings', {
    id: props.orgId,
    allowedAiVersions: localAllowedVersions.value,
    defaultAiVersion: localDefaultVersion.value
  })
  saving.value = false
}
</script>
